import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  People,
  SwapHoriz,
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import { UserAnalytics } from '../../types';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 100,
    height: 100,
    background: 'linear-gradient(135deg, rgba(79, 209, 199, 0.1), rgba(56, 178, 172, 0.05))',
    borderRadius: '50%',
    transform: 'translate(30%, -30%)',
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginTop: theme.spacing(1),
}));

const StatIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  '& svg': {
    fontSize: 40,
    color: '#4FD1C7',
    opacity: 0.7,
  },
}));

const AdminAnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const data = await adminService.getUserAnalytics();
        setAnalytics(data);
      } catch (err) {
        console.error('Error loading analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!analytics) {
    return <Alert severity="warning" sx={{ m: 2 }}>No analytics data available</Alert>;
  }

  const stats = [
    {
      title: 'Total Users',
      value: analytics.totalUsers,
      icon: <People />,
      subtitle: `+${analytics.newUsersThisMonth} this month`,
    },
    {
      title: 'Active Users',
      value: analytics.activeUsers,
      icon: <People />,
      subtitle: `${Math.round((analytics.activeUsers / analytics.totalUsers) * 100)}% engagement`,
    },
    {
      title: 'Total Transactions',
      value: analytics.totalTransactions,
      icon: <SwapHoriz />,
      subtitle: `${analytics.newUsersToday} today`,
    },
    {
      title: 'Points Tracked',
      value: `${(analytics.totalPointsTracked / 1000000).toFixed(1)}M`,
      icon: <TrendingUp />,
      subtitle: 'Total points value',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Platform Analytics
      </Typography>

      {/* Key Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard>
              <StatIcon>{stat.icon}</StatIcon>
              <Typography color="textSecondary" variant="body2" sx={{ mb: 1 }}>
                {stat.title}
              </Typography>
              <StatValue>{stat.value}</StatValue>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                {stat.subtitle}
              </Typography>
            </StatCard>
          </Grid>
        ))}
      </Grid>

      {/* User Growth Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                User Growth
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Today</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {analytics.newUsersToday}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((analytics.newUsersToday / 100) * 100, 100)} 
                  sx={{ mb: 2 }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">This Week</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {analytics.newUsersThisWeek}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((analytics.newUsersThisWeek / 100) * 100, 100)} 
                  sx={{ mb: 2 }}
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">This Month</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {analytics.newUsersThisMonth}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((analytics.newUsersThisMonth / 100) * 100, 100)} 
                />
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                User Engagement
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Engagement Rate</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4FD1C7' }}>
                    {Math.round((analytics.activeUsers / analytics.totalUsers) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(analytics.activeUsers / analytics.totalUsers) * 100}
                  sx={{ 
                    backgroundColor: 'rgba(79, 209, 199, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #38B2AC 0%, #4FD1C7 100%)',
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'rgba(79, 209, 199, 0.1)', p: 2, borderRadius: 1 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Active Users</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{analytics.activeUsers}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Inactive Users</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {analytics.totalUsers - analytics.activeUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Footer Info */}
      <Typography variant="caption" color="textSecondary">
        Last updated: {analytics.lastUpdated?.toLocaleString()}
      </Typography>
    </Box>
  );
};

export default AdminAnalyticsPage;
